/**
 * useTaskViewerTracking - Real-time clash detection for work items
 * 
 * Tracks who is currently viewing a work item (email task, invoice, quote, etc.)
 * to prevent conflicts when multiple users try to work on the same item.
 * 
 * Uses WebSocket for real-time presence with Redis-backed ephemeral storage.
 * 
 * @example
 * const { viewers, isViewing, startViewing, stopViewing } = useTaskViewerTracking({
 *   taskId: selectedItem?.id,
 *   enabled: showModal && !!selectedItem,
 *   userId: user?.id,
 *   userName: user?.name || user?.email,
 *   onViewerJoined: (viewer) => notify.info(`${viewer.user_name} is also viewing`),
 *   onViewerLeft: (viewer) => notify.info(`${viewer.user_name} stopped viewing`),
 * });
 */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { notify } from '@/lib/notifications';
import { wsClient } from '@/lib/websocket';

export interface TaskViewer {
  user_id: number;
  user_name: string;
  first_name?: string;
  last_name?: string;
  joined_at?: string;
}

interface UseTaskViewerTrackingOptions {
  /** The work item ID to track viewers for (task, invoice, quote, etc.) */
  taskId?: string;
  /** Whether tracking is enabled (typically when modal is open) */
  enabled?: boolean;
  /** Current user's ID */
  userId?: number;
  /** Current user's display name */
  userName?: string;
  /** Callback when another user starts viewing */
  onViewerJoined?: (viewer: TaskViewer) => void;
  /** Callback when another user stops viewing */
  onViewerLeft?: (viewer: TaskViewer) => void;
  /** Show built-in toast notifications (default: true) */
  showToasts?: boolean;
}

interface UseTaskViewerTrackingResult {
  /** List of users currently viewing this item (excluding current user) */
  viewers: TaskViewer[];
  /** All viewers including current user */
  allViewers: TaskViewer[];
  /** Whether current user is actively viewing */
  isViewing: boolean;
  /** Number of other users viewing */
  otherViewersCount: number;
  /** Manually start viewing (usually automatic) */
  startViewing: () => void;
  /** Manually stop viewing (usually automatic on unmount) */
  stopViewing: () => void;
  /** Connection status */
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const HEARTBEAT_INTERVAL = 60000; // 60 seconds

export function useTaskViewerTracking({
  taskId,
  enabled = true,
  userId,
  userName,
  onViewerJoined,
  onViewerLeft,
  showToasts = true,
}: UseTaskViewerTrackingOptions): UseTaskViewerTrackingResult {
  const [viewers, setViewers] = useState<TaskViewer[]>([]);
  const [isViewing, setIsViewing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const previousViewersRef = useRef<TaskViewer[]>([]);
  const enabledRef = useRef(enabled);
  const onViewerJoinedRef = useRef(onViewerJoined);
  const onViewerLeftRef = useRef(onViewerLeft);
  const showToastsRef = useRef(showToasts);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    onViewerJoinedRef.current = onViewerJoined;
  }, [onViewerJoined]);

  useEffect(() => {
    onViewerLeftRef.current = onViewerLeft;
  }, [onViewerLeft]);

  useEffect(() => {
    showToastsRef.current = showToasts;
  }, [showToasts]);

  // Clean up heartbeat interval
  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Send message via WebSocket
  const sendMessage = useCallback((type: string, data: Record<string, any>) => {
    return wsClient.send({ type, ...data });
  }, []);

  // Start viewing a task
  const startViewing = useCallback(() => {
    if (!taskId || !userId) return;

    const success = sendMessage('task:viewer:join', { task_id: taskId, user_name: userName });
    if (success) {
      currentTaskIdRef.current = taskId;

      // Start heartbeat
      clearHeartbeat();
      heartbeatIntervalRef.current = setInterval(() => {
        if (currentTaskIdRef.current) {
          sendMessage('task:viewer:heartbeat', { task_id: currentTaskIdRef.current });
        }
      }, HEARTBEAT_INTERVAL);
    }
  }, [taskId, userId, userName, sendMessage, clearHeartbeat]);

  // Stop viewing a task
  const stopViewing = useCallback(() => {
    if (!currentTaskIdRef.current) return;
    
    sendMessage('task:viewer:leave', { task_id: currentTaskIdRef.current });
    currentTaskIdRef.current = null;
    setIsViewing(false);
    setViewers([]);
    previousViewersRef.current = [];
    clearHeartbeat();
  }, [sendMessage, clearHeartbeat]);

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data: Record<string, any>) => {
    switch (data.type) {
        case 'task:viewer:joined':
          // Confirmation that we joined, with current viewers
          if (data.task_id === currentTaskIdRef.current && data.viewers) {
            setIsViewing(true);
            const filteredViewers = data.viewers.filter((v: TaskViewer) => v.user_id !== userId);
            setViewers(filteredViewers);
            previousViewersRef.current = filteredViewers;
            
            // Show toast if there are already other viewers when we join
            if (showToastsRef.current && filteredViewers.length > 0) {
              const names = filteredViewers.map((v: TaskViewer) => v.user_name).join(', ');
              notify.info(`👁️ Also viewing: ${names}`, { duration: 5000 });
            }
          }
          break;
          
        case 'task:viewer:update':
          // Broadcast when viewers change (someone joined or left)
          if ((data.task_id === currentTaskIdRef.current || data.taskId === currentTaskIdRef.current) && data.viewers) {
            const filteredViewers = data.viewers.filter((v: TaskViewer) => v.user_id !== userId);
            const previousIds = new Set(previousViewersRef.current.map(v => v.user_id));
            const currentIds = new Set(filteredViewers.map((v: TaskViewer) => v.user_id));
            
            // Find who joined
            const joined = filteredViewers.filter((v: TaskViewer) => !previousIds.has(v.user_id));
            // Find who left
            const left = previousViewersRef.current.filter(v => !currentIds.has(v.user_id));
            
            // Trigger callbacks and toasts for joins
            joined.forEach((viewer: TaskViewer) => {
              onViewerJoinedRef.current?.(viewer);
              if (showToastsRef.current) {
                notify.info(`👁️ ${viewer.user_name} started viewing this item`, { duration: 4000 });
              }
            });
            
            // Trigger callbacks and toasts for leaves
            left.forEach((viewer: TaskViewer) => {
              onViewerLeftRef.current?.(viewer);
              if (showToastsRef.current) {
                notify.info(`${viewer.user_name} stopped viewing`, { duration: 3000 });
              }
            });
            
            setViewers(filteredViewers);
            previousViewersRef.current = filteredViewers;
          }
          break;
          
        case 'task:viewer:left':
          // Confirmation that we left
          if (data.task_id === currentTaskIdRef.current) {
            setViewers([]);
            previousViewersRef.current = [];
          }
          break;
          
        case 'task:viewer:error':
          console.error('[TaskViewer] Error:', data.error);
          break;
    }
  }, [userId]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!enabledRef.current || !userId) {
      return;
    }

    void wsClient.connect().catch((error) => {
      console.error('[TaskViewer] Failed to connect shared WebSocket:', error);
      if (mountedRef.current) {
        setConnectionStatus('error');
      }
    });
  }, [userId]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    // Stop viewing before disconnecting
    stopViewing();
    clearHeartbeat();
    
    setConnectionStatus('disconnected');
  }, [clearHeartbeat, stopViewing]);

  useEffect(() => {
    const unsubscribeConnection = wsClient.onConnection((status) => {
      if (!mountedRef.current || !enabledRef.current) {
        return;
      }

      if (status === 'connected') {
        setConnectionStatus('connected');
        return;
      }

      if (status === 'connecting' || status === 'reconnecting') {
        setConnectionStatus('connecting');
      } else {
        setConnectionStatus('disconnected');
      }

      clearHeartbeat();
      setIsViewing(false);
      setViewers([]);
      previousViewersRef.current = [];
    });

    const unsubscribeLegacy = wsClient.onLegacyMessage(handleMessage);

    return () => {
      unsubscribeConnection();
      unsubscribeLegacy();
    };
  }, [clearHeartbeat, handleMessage]);

  // Connect/disconnect based on enabled state
  useEffect(() => {
    mountedRef.current = true;
    
    if (enabled && userId) {
      const connectTimeout = window.setTimeout(() => {
        connect();
      }, 0);

      return () => {
        window.clearTimeout(connectTimeout);
        mountedRef.current = false;
        disconnect();
      };
    } else {
      queueMicrotask(() => {
        disconnect();
      });
    }
    
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, userId, connect, disconnect]);

  // When task changes while connected, update viewing
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    
    // If we're viewing a different task, leave the old one and join the new one
    if (taskId) {
      if (currentTaskIdRef.current && taskId !== currentTaskIdRef.current) {
        sendMessage('task:viewer:leave', { task_id: currentTaskIdRef.current });
      }

      const startViewingTimeout = window.setTimeout(() => {
        startViewing();
      }, 0);

      return () => {
        window.clearTimeout(startViewingTimeout);
      };
    } else if (currentTaskIdRef.current) {
      queueMicrotask(() => {
        stopViewing();
      });
    }
  }, [taskId, connectionStatus, startViewing, stopViewing, sendMessage]);

  // Calculate other viewers count
  const otherViewersCount = viewers.filter(v => v.user_id !== userId).length;

  // All viewers including current user
  const allViewers = isViewing && userId && userName
    ? [...viewers, { user_id: userId, user_name: userName }]
    : viewers;

  return {
    viewers,
    allViewers,
    isViewing,
    otherViewersCount,
    startViewing,
    stopViewing,
    connectionStatus,
  };
}

export default useTaskViewerTracking;
